import type { HistoryTimelineItem } from '../types/types'

/** This endpoint is scoped to one sample. Pair only unique lifecycle creation/insert
 * records, never updates or entries merely sharing a displayed minute. Sample
 * events use the internal DB ID while sample audit entries use the bag number. */
export const presentHistoryItems = (
  items: HistoryTimelineItem[]
): HistoryTimelineItem[] => {
  const creationKind = (item: HistoryTimelineItem) =>
    item.entityType === 'sample'
      ? 'SampleCreated'
      : item.entityType === 'requisition'
        ? 'RequisitionCreated'
        : undefined
  const matches = (event: HistoryTimelineItem, audit: HistoryTimelineItem) =>
    event.source === 'event' &&
    event.kind === creationKind(event) &&
    event.metadata?.additionalInfo !== 'lifecycle:compromise-resolved' &&
    audit.source === 'audit' &&
    audit.kind === 'insert' &&
    event.entityType === audit.entityType &&
    (event.entityType === 'sample' || event.entityId === audit.entityId) &&
    (audit.changes?.length ?? 0) === 0 &&
    !audit.description?.trim()
  const hiddenIds = new Set<string>()
  items
    .filter((item) => item.source === 'event')
    .forEach((event) => {
      const audits = items.filter((audit) => matches(event, audit))
      if (
        audits.length === 1 &&
        items.filter((other) => matches(other, audits[0])).length === 1 &&
        !items.some(
          (other) =>
            other.entityType === event.entityType &&
            (event.entityType === 'sample' ||
              other.entityId === event.entityId) &&
            ['delete', 'SampleDeleted', 'RequisitionDeleted'].includes(
              other.kind
            )
        )
      ) {
        hiddenIds.add(audits[0].id)
      }
    })
  return items.filter((item) => !hiddenIds.has(item.id))
}

export const historyPresentationTitle = (item: HistoryTimelineItem): string => {
  if (item.source !== 'audit') return item.title
  const entity = {
    sample: 'Prøve',
    requisition: 'Rekvisisjon',
    result: 'Resultat'
  }[item.entityType]
  const operation: Record<string, string> = {
    insert: 'opprettet',
    update: 'oppdatert',
    delete: 'slettet'
  }
  return entity && operation[item.kind]
    ? `${entity} ${operation[item.kind]}`
    : item.title
}
